import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { jobApi } from '../../api/jobApi'
import { Job } from '../../types'
import { toast } from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import { Plus, Eye, Users, Loader2, CheckCircle, Clock, XCircle, Lock } from 'lucide-react'
import { clsx } from 'clsx'

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'badge bg-yellow-50 text-yellow-700',
  ACTIVE: 'badge bg-green-50 text-green-700',
  REJECTED: 'badge bg-red-50 text-red-700',
  CLOSED: 'badge bg-gray-100 text-gray-600',
}

const STATUS_ICON: Record<string, React.ReactNode> = {
  PENDING: <Clock size={12} />,
  ACTIVE: <CheckCircle size={12} />,
  REJECTED: <XCircle size={12} />,
  CLOSED: <Lock size={12} />,
}

export default function MyJobsPage() {
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    jobApi
      .getMyPosts()
      .then(({ data }) => setJobs((data.data as any)?.content || data.data || []))
      .catch(() => toast.error('Failed to load posts'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">My Job Posts</h1>
        <Link to="/create-job" className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Post a Job
        </Link>
      </div>

      {jobs.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-gray-400 text-lg">You haven't posted any jobs yet</p>
          <Link to="/create-job" className="btn-primary mt-4 inline-flex items-center gap-2">
            <Plus size={16} /> Post your first job
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-gray-900">
                      <Link to={`/jobs/${job.id}`} className="hover:text-primary-600 no-underline">
                        {job.title}
                      </Link>
                    </h2>
                    <span className={clsx(STATUS_STYLE[job.status], 'flex items-center gap-1')}>
                      {STATUS_ICON[job.status]} {job.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{job.company.name}</p>

                  {job.status === 'REJECTED' && job.rejectionReason && (
                    <p className="text-xs text-red-500 mt-2 bg-red-50 px-2 py-1 rounded">
                      Rejected: {job.rejectionReason}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-6 mt-4 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Users size={14} /> {job.applicationsCount} applicants
                </span>
                <span className="flex items-center gap-1.5">
                  <Eye size={14} /> {job.likesCount} likes
                </span>
                <span className="text-gray-400">
                  {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                </span>
              </div>

              {job.status === 'ACTIVE' && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                  <Link to={`/jobs/${job.id}/applications`} className="btn-secondary text-xs">
                    View Applications ({job.applicationsCount})
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
